import { getPtBookingRange } from "../pt-session-slots";

export function intervalsOverlapMs(a0, a1, b0, b1) {
  return a0 < b1 && b0 < a1;
}

/** Whether a booking (calendar entry or API row) overlaps `start`-`end`. */
export function bookingOverlapsRange(booking, start, end) {
  const range = getPtBookingRange(booking);
  if (!range) return false;
  return intervalsOverlapMs(
    range.start.getTime(),
    range.end.getTime(),
    start.getTime(),
    end.getTime()
  );
}

/**
 * Split one availability-window occurrence (PT session or consult) into booked
 * slot segments and the free gaps between them, so a booking shows at its own
 * slot time instead of the whole window's.
 */
function buildWindowOccurrenceSegments(occurrence, bookings, record) {
  const occurrenceStart = occurrence?.start?.getTime();
  const occurrenceEnd = occurrence?.end?.getTime();
  if (!Number.isFinite(occurrenceStart) || !Number.isFinite(occurrenceEnd)) {
    return [];
  }
  const bookingGroups = new Map();

  for (const booking of bookings) {
    const bookingRange = getPtBookingRange(booking);
    const bookingStart = bookingRange?.start?.getTime();
    const bookingEnd = bookingRange?.end?.getTime();
    if (!Number.isFinite(bookingStart) || !Number.isFinite(bookingEnd)) {
      continue;
    }

    const start = Math.max(occurrenceStart, bookingStart);
    const end = Math.min(occurrenceEnd, bookingEnd);
    if (end <= start) continue;
    const key = `${start}-${end}`;
    const group = bookingGroups.get(key) ?? {
      start,
      end,
      bookings: [],
    };
    group.bookings.push(booking);
    bookingGroups.set(key, group);
  }

  const bookedGroups = [...bookingGroups.values()].sort(
    (a, b) => a.start - b.start
  );
  const segments = [];
  let cursor = occurrenceStart;

  for (const group of bookedGroups) {
    if (group.start > cursor) {
      segments.push({
        type: "free",
        start: cursor,
        end: group.start,
      });
    }
    segments.push({ type: "booked", ...group });
    cursor = Math.max(cursor, group.end);
  }
  if (cursor < occurrenceEnd) {
    segments.push({ type: "free", start: cursor, end: occurrenceEnd });
  }

  return segments.map((segment, index) => {
    const start = new Date(segment.start);
    const end = new Date(segment.end);
    if (segment.type === "free") {
      return {
        ...occurrence,
        key: `${occurrence.key}-free-${index}`,
        start,
        end,
        raw: { ...record, enrolled_count: 0, booked_members: [] },
        fromBooking: true,
      };
    }
    return {
      ...occurrence,
      key: `${occurrence.key}-booked-${segment.start}-${segment.end}`,
      start,
      end,
      raw: {
        ...record,
        enrolled_count: segment.bookings.length,
        booked_members: segment.bookings.map((booking) => booking.member),
      },
      fromBooking: true,
    };
  });
}

/**
 * Split every window occurrence of one PT session / consult by its bookings.
 * Bookings no window covers are returned so they can still show as slot cards.
 */
export function splitWindowsByBookings(occurrences, bookings, record) {
  const consumedBookingIds = new Set();
  const segments = [];
  for (const occurrence of occurrences) {
    const availableBookings = bookings.filter(
      (booking) => !consumedBookingIds.has(booking.bookingId)
    );
    for (const segment of buildWindowOccurrenceSegments(
      occurrence,
      availableBookings,
      record
    )) {
      segments.push(segment);
      for (const booking of availableBookings) {
        if (bookingOverlapsRange(booking, segment.start, segment.end)) {
          consumedBookingIds.add(booking.bookingId);
        }
      }
    }
  }
  return {
    segments,
    uncoveredBookings: bookings.filter(
      (booking) => !consumedBookingIds.has(booking.bookingId)
    ),
  };
}
